import React, { useEffect, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import Avatar from "@/components/Avatar";
import { toast } from "sonner";
import { X, Globe, Copy, Trash2, ExternalLink } from "lucide-react";

// Modal for publishing / managing a Colleague Card (the public, opt-in page).
// `persona` is the persona object; onClose() is called when the user closes the modal.
export default function CardEditor({ persona, onClose }) {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tagline, setTagline] = useState("");
  const [story, setStory] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/personas/${persona.persona_id}/card`);
        if (!active) return;
        setCard(data);
        setTagline(data.tagline_user || "");
        setStory(data.story_oneliner || "");
      } catch (e) {
        // 404 = no card yet; that's fine
        if (e?.response?.status !== 404 && active) {
          toast.error(formatApiError(e));
        }
        if (active) {
          setTagline(persona.tagline || "");
          setStory("");
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [persona]);

  const publicUrl = card ? `${window.location.origin}/c/${card.slug}` : "";

  const publish = async () => {
    if (story.trim().length < 10) {
      toast.error("Add a short story (10+ characters)");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.put(`/personas/${persona.persona_id}/card`, {
        tagline_user: tagline || null,
        story_oneliner: story,
        public: true,
      });
      setCard(data);
      toast.success("Colleague Card published");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const unpublish = async () => {
    if (!card) return;
    if (!window.confirm("Take down this Colleague Card?")) return;
    setBusy(true);
    try {
      await api.delete(`/personas/${persona.persona_id}/card`);
      setCard(null);
      toast.success("Card unpublished");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <div
      data-testid="card-editor-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#2D2926]/40 backdrop-blur-sm p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        data-testid="card-editor"
        className="bg-[#FBF9F6] rounded-2xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto p-8 anim-fade-up"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#9A938A] mb-1">
              Colleague Card
            </p>
            <h2 className="font-serif text-3xl text-[#2D2926]">
              Share {persona.name} with the world
            </h2>
            <p className="text-sm text-[#6B655D] mt-2 max-w-sm">
              A public, opt-in page. No conversation contents are ever shared — just your
              persona, your story.
            </p>
          </div>
          <button
            type="button"
            data-testid="card-close-btn"
            onClick={onClose}
            className="text-[#6B655D] hover:text-[#2D2926] rounded-full p-2 hover:bg-[#F2EFE9]"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        {/* Preview */}
        <div className="surface-card p-5 mb-6 bg-white">
          <div className="flex items-center gap-4">
            <Avatar src={persona.avatar_url} name={persona.name} size={64} />
            <div className="min-w-0">
              <p className="font-serif text-2xl text-[#2D2926] truncate">
                {persona.name}
              </p>
              <p className="text-xs uppercase tracking-wider text-[#9A938A]">
                {persona.role || "Colleague"}
              </p>
            </div>
          </div>
          {tagline && (
            <p className="italic text-[#6B655D] mt-3 text-sm">"{tagline}"</p>
          )}
          {story && (
            <p className="font-serif text-xl text-[#2D2926] mt-3 leading-snug">
              {story}
            </p>
          )}
        </div>

        {loading ? (
          <p className="text-[#6B655D]">Loading…</p>
        ) : (
          <>
            <label className="block text-xs uppercase tracking-wider text-[#9A938A] mb-2">
              Tagline (optional)
            </label>
            <input
              data-testid="card-tagline-input"
              value={tagline}
              onChange={(e) => setTagline(e.target.value.slice(0, 140))}
              placeholder={persona.tagline || "e.g. The colleague who tells me the truth, kindly."}
              className="w-full bg-white border border-[#E6E2D8] rounded-xl px-4 py-3 mb-5 focus:outline-none focus:ring-2 focus:ring-[#D4A574]"
            />

            <label className="block text-xs uppercase tracking-wider text-[#9A938A] mb-2">
              Your one-line story
            </label>
            <textarea
              data-testid="card-story-input"
              value={story}
              onChange={(e) => setStory(e.target.value.slice(0, 240))}
              placeholder="e.g. Grace helped me reframe a $400k budget ask into something my CFO actually approved."
              rows={3}
              className="w-full bg-white border border-[#E6E2D8] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#D4A574] leading-relaxed"
            />
            <p className="text-xs text-[#9A938A] mt-1 mb-6">
              {story.length}/240 · no conversation contents are included.
            </p>

            {card?.public && (
              <div className="surface-card p-4 bg-[#F2EFE9] mb-4 flex items-center gap-3">
                <Globe size={18} strokeWidth={1.5} className="text-[#C29260]" />
                <code
                  data-testid="card-public-url"
                  className="flex-1 text-sm text-[#2D2926] truncate"
                >
                  {publicUrl}
                </code>
                <button
                  type="button"
                  data-testid="card-copy-btn"
                  onClick={copyLink}
                  className="text-[#6B655D] hover:text-[#2D2926] rounded-full p-2 hover:bg-white"
                  aria-label="Copy link"
                >
                  <Copy size={14} strokeWidth={1.5} />
                </button>
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="card-open-btn"
                  className="text-[#6B655D] hover:text-[#2D2926] rounded-full p-2 hover:bg-white"
                  aria-label="Open"
                >
                  <ExternalLink size={14} strokeWidth={1.5} />
                </a>
              </div>
            )}

            {card?.public && card.slug && (
              <div className="mb-6">
                <p className="text-xs uppercase tracking-wider text-[#9A938A] mb-2">
                  Social preview (LinkedIn, Twitter, iMessage)
                </p>
                <img
                  data-testid="card-og-preview"
                  src={`${API}/cards/${card.slug}/og.png?t=${encodeURIComponent(card.updated_at || "")}`}
                  alt="Social share preview"
                  className="w-full rounded-xl border border-[#E6E2D8] shadow-sm"
                />
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                data-testid="card-publish-btn"
                onClick={publish}
                disabled={busy || story.trim().length < 10}
                className="inline-flex items-center gap-1.5 bg-[#2D2926] text-white hover:bg-[#1A1715] rounded-full px-5 py-2.5 text-sm transition-colors disabled:opacity-60"
              >
                <Globe size={14} strokeWidth={1.5} />
                {busy ? "Saving…" : card?.public ? "Update" : "Publish"}
              </button>
              {card?.public && (
                <button
                  type="button"
                  data-testid="card-unpublish-btn"
                  onClick={unpublish}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 bg-[#F2EFE9] text-[#D98074] hover:bg-[#FBEEEA] rounded-full px-5 py-2.5 text-sm transition-colors"
                >
                  <Trash2 size={14} strokeWidth={1.5} /> Unpublish
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
